import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Text } from "@nbs/ui";
import { RoleContextBanner } from "@/features/rbac/RoleContextBanner";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import type { Capability } from "@/services/auth";

type DemoAccountRow = {
  email: string;
  displayName: string;
  role: string;
  disabled: boolean;
};

const ACTION_CAPS: readonly Capability[] = [
  "action.reviewExtraction",
  "action.scientificValidate",
  "action.publishMeasure",
  "action.manageUsers",
  "action.manageRoles",
];

const DATA_CAPS: readonly Capability[] = [
  "data.publishedMeasures",
  "data.nonPublishedMeasures",
  "data.proposals",
  "data.fullProvenance",
  "export.published",
  "export.internal",
];

export function SettingsPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const permissions = usePermissions();
  const [accounts, setAccounts] = useState<DemoAccountRow[] | null>(null);
  const [accountsError, setAccountsError] = useState(false);

  const canManageUsers = permissions.can("action.manageUsers");

  useEffect(() => {
    if (!canManageUsers) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/demo/accounts.json", { cache: "no-store" });
        if (!res.ok) throw new Error("unavailable");
        const body = (await res.json()) as { accounts?: DemoAccountRow[] };
        if (!cancelled) setAccounts(body.accounts ?? []);
      } catch {
        if (!cancelled) setAccountsError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageUsers]);

  return (
    <article className="nbs-settings-page" data-testid="settings-page">
      <header className="nbs-settings-page__header">
        <Heading level={1}>{t("settings.title")}</Heading>
        <Text tone="muted">{t("settings.description")}</Text>
      </header>

      <RoleContextBanner />

      <section className="nbs-settings-card" aria-labelledby="settings-profile-heading">
        <Heading level={2} id="settings-profile-heading">
          {t("settings.profile.title")}
        </Heading>
        <dl className="nbs-settings-card__meta">
          <div>
            <dt>{t("settings.profile.user")}</dt>
            <dd>{auth.user?.displayName ?? "—"}</dd>
          </div>
          <div>
            <dt>{t("settings.profile.email")}</dt>
            <dd>{auth.user?.email ?? auth.user?.id ?? "—"}</dd>
          </div>
          <div>
            <dt>{t("settings.profile.role")}</dt>
            <dd>{t(`rbac.roles.${permissions.productLabel}`)}</dd>
          </div>
        </dl>
      </section>

      <section className="nbs-settings-card" aria-labelledby="settings-caps-heading">
        <Heading level={2} id="settings-caps-heading">
          {t("settings.capabilities.title")}
        </Heading>
        <Text tone="muted" size="sm">
          {t("settings.capabilities.lead")}
        </Text>
        <div className="nbs-settings-caps">
          <CapabilityGroup
            title={t("settings.capabilities.actions")}
            caps={ACTION_CAPS}
            can={permissions.can}
          />
          <CapabilityGroup
            title={t("settings.capabilities.data")}
            caps={DATA_CAPS}
            can={permissions.can}
          />
        </div>
      </section>

      {canManageUsers ? (
        <section className="nbs-settings-card" aria-labelledby="settings-accounts-heading">
          <Heading level={2} id="settings-accounts-heading">
            {t("settings.accounts.title")}
          </Heading>
          <Text tone="muted" size="sm">
            {t("settings.accounts.lead")}
          </Text>
          {accountsError ? (
            <Text role="alert" className="nbs-text--error">
              {t("settings.accounts.error")}
            </Text>
          ) : null}
          {accounts ? (
            <div className="nbs-catalog-table-wrap">
              <table className="nbs-catalog-table">
                <thead>
                  <tr>
                    <th>{t("settings.accounts.columns.name")}</th>
                    <th>{t("settings.accounts.columns.email")}</th>
                    <th>{t("settings.accounts.columns.role")}</th>
                    <th>{t("settings.accounts.columns.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.email}>
                      <td>{account.displayName}</td>
                      <td>{account.email}</td>
                      <td>{account.role}</td>
                      <td>
                        {account.disabled
                          ? t("settings.accounts.disabled")
                          : t("settings.accounts.active")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !accountsError ? (
            <Text tone="muted">{t("settings.accounts.loading")}</Text>
          ) : null}
        </section>
      ) : (
        <section className="nbs-settings-card" aria-labelledby="settings-readonly-heading">
          <Heading level={2} id="settings-readonly-heading">
            {t("settings.accounts.title")}
          </Heading>
          <Text tone="muted">{t("settings.accounts.forbidden")}</Text>
        </section>
      )}
    </article>
  );
}

function CapabilityGroup({
  title,
  caps,
  can,
}: {
  title: string;
  caps: readonly Capability[];
  can: (cap: Capability) => boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="nbs-settings-caps__group">
      <h3 className="nbs-settings-caps__title">{title}</h3>
      <ul>
        {caps.map((cap) => {
          const allowed = can(cap);
          return (
            <li
              key={cap}
              className={allowed ? "nbs-settings-caps__item--on" : "nbs-settings-caps__item--off"}
            >
              <span aria-hidden="true">{allowed ? "●" : "○"}</span>
              <span>{t(`settings.capabilities.labels.${cap}`, { defaultValue: cap })}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
